import type { CarouselDoc, CarouselLayer, Rect, SplitterDoc } from './types';
import { TILE_ASPECTS } from './presets';
import { SPLIT_TILE_EDGE } from './doc/defaults';
import { safeName } from './utils';
import { boundingBox } from './geom';

/**
 * The single source of truth for splitter geometry.
 *
 * Same discipline as `computeLayout`: pure, no DOM, explicit pixel sizes, and
 * called by the preview and the exporter with different numbers — which is
 * what guarantees the tiles you download match the tiles you saw.
 */

export interface GridFrame {
  tileW: number;
  tileH: number;
  cols: number;
  rows: number;
  /** the framing rectangle — never allocated as a bitmap */
  width: number;
  height: number;
}

export interface TileRect extends Rect {
  col: number;
  row: number;
  index: number;
}

/** Base pixel size of one tile, with `longEdge` as its longer side. */
export function tileSize(aspectId: string, longEdge = SPLIT_TILE_EDGE) {
  const preset = TILE_ASPECTS.find((a) => a.id === aspectId) ?? TILE_ASPECTS[0];
  const ratio = preset.ratio;
  return ratio >= 1
    ? { w: longEdge, h: Math.round(longEdge / ratio) }
    : { w: Math.round(longEdge * ratio), h: longEdge };
}

export function gridFrame(doc: SplitterDoc, scale = 1): GridFrame {
  const { w, h } = tileSize(doc.tileAspect);
  const tileW = w * scale;
  const tileH = h * scale;
  return {
    tileW,
    tileH,
    cols: doc.cols,
    rows: doc.rows,
    width: tileW * doc.cols,
    height: tileH * doc.rows,
  };
}

/** Every tile, in reading order: left to right, top to bottom. */
export function tileRects(frame: GridFrame): TileRect[] {
  const out: TileRect[] = [];
  for (let row = 0; row < frame.rows; row++) {
    for (let col = 0; col < frame.cols; col++) {
      out.push({
        x: col * frame.tileW,
        y: row * frame.tileH,
        w: frame.tileW,
        h: frame.tileH,
        col,
        row,
        index: row * frame.cols + col,
      });
    }
  }
  return out;
}

/**
 * The order the tiles should be *uploaded* in, as indices into reading order.
 *
 * A profile grid fills newest-first, left to right and top to bottom, so
 * posting in reverse reading order is what reassembles the picture. Getting
 * this backwards is the single most common way people ruin a puzzle grid,
 * which is why it is a named function with a test rather than an inline
 * `.reverse()`.
 */
export function tileOrder(count: number, mode: 'instagram' | 'reading'): number[] {
  const reading = Array.from({ length: count }, (_, i) => i);
  return mode === 'instagram' ? reading.reverse() : reading;
}

/**
 * Zero-padded so the files sort into upload order in any file manager —
 * `grid-01.png` first, whatever that tile's position in the picture is.
 */
export function tileFilename(
  base: string,
  seq: number,
  total: number,
  tile: { row: number; col: number },
  ext: string,
) {
  const width = String(total).length;
  const n = String(seq).padStart(width, '0');
  return `${safeName(base)}-${n}-r${tile.row + 1}c${tile.col + 1}.${ext}`;
}

/* ── Carousel ──────────────────────────────────────────────────────────── */

export interface SlideFrame {
  slideW: number;
  slideH: number;
  slides: number;
  /** the whole strip — laid out and edited as one continuous canvas */
  width: number;
  height: number;
}

export function slideFrame(doc: CarouselDoc, scale = 1): SlideFrame {
  const slideW = doc.slideW * scale;
  const slideH = doc.slideH * scale;
  return {
    slideW,
    slideH,
    slides: doc.slides,
    width: slideW * doc.slides,
    height: slideH,
  };
}

export function slideRects(frame: SlideFrame): Rect[] {
  return Array.from({ length: frame.slides }, (_, i) => ({
    x: i * frame.slideW,
    y: 0,
    w: frame.slideW,
    h: frame.slideH,
  }));
}

/**
 * Which layers show up on a given slide.
 *
 * A layer straddling a boundary is returned for *both* neighbours — that is
 * the whole point of the tool, and the renderer draws it at the same strip
 * coordinates in each, so the two exported files continue into one another.
 */
export function layersOnSlide(doc: CarouselDoc, index: number): CarouselLayer[] {
  const frame = slideFrame(doc);
  const left = index * frame.slideW;
  const right = left + frame.slideW;
  return doc.layers.filter((layer) => {
    if (layer.hidden) return false;
    const b = boundingBox(layer, layer.rotation);
    return b.x < right && b.x + b.w > left;
  });
}

export function slideFilename(base: string, seq: number, total: number, ext: string) {
  const width = String(total).length;
  return `${safeName(base)}-${String(seq).padStart(width, '0')}.${ext}`;
}
